import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { setAccessToken } from '../api/client'
import {
  getTranscript,
  getAudioUrl,
  editSegment,
  splitSegment,
  exportTranscript,
  getSpeakerNames,
  setSpeakerName,
  getTranscriptAnalysis,
  correctWord,
  updateAbbreviationMode,
} from '../api/transcript'
import type { Transcript, TranscriptAnalysis } from '../types'
import AudioPlayer from '../components/AudioPlayer'
import TranscriptList from '../components/TranscriptList'
import SpeakerList from '../components/SpeakerList'
import AbbreviationPanel from '../components/AbbreviationPanel'

export default function TranscriptPage() {
  const { token, projectId: projectIdParam } = useParams<{ token?: string; projectId?: string }>()
  const queryClient = useQueryClient()
  const [projectId, setProjectId] = useState<number | null>(
    projectIdParam ? parseInt(projectIdParam, 10) : null
  )
  const [speakerNames, setSpeakerNames] = useState<Record<string, string>>({})

  // Token-based access: set access token on mount
  useEffect(() => {
    if (token) {
      setAccessToken(token)
    }
  }, [token])

  // Token-based: get project associated with this token
  const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['project', token],
    queryFn: async () => {
      const { default: apiClient } = await import('../api/client')
      const response = await apiClient.get('/user/token-project')
      setProjectId(response.data.id)
      return response.data
    },
    enabled: !!token && !projectIdParam,
    retry: false,
  })

  // Get transcript
  const { data: transcript, isLoading: transcriptLoading } = useQuery({
    queryKey: ['transcript', projectId],
    queryFn: () => getTranscript(projectId!),
    enabled: !!projectId,
  })

  // Get audio URL
  const { data: audioData } = useQuery({
    queryKey: ['audioUrl', projectId],
    queryFn: () => getAudioUrl(projectId!),
    enabled: !!projectId,
  })

  // Get saved speaker names
  const { data: savedSpeakerNames } = useQuery({
    queryKey: ['speakerNames', projectId],
    queryFn: () => getSpeakerNames(projectId!),
    enabled: !!projectId,
  })

  // Get transcript analysis (word-level data)
  const { data: analysis } = useQuery({
    queryKey: ['transcriptAnalysis', projectId],
    queryFn: () => getTranscriptAnalysis(projectId!),
    enabled: !!projectId,
  })

  // Update local state when saved names are loaded, reconstruct custom speakers
  useEffect(() => {
    if (savedSpeakerNames) {
      setSpeakerNames(savedSpeakerNames)
      // Reconstruct custom speakers from saved names not present in transcript speakers
      if (transcript?.speakers) {
        const transcriptSpeakerIds = new Set(transcript.speakers.map(s => s.id))
        const restored = Object.entries(savedSpeakerNames)
          .filter(([id]) => !transcriptSpeakerIds.has(id))
          .map(([id, name]) => ({ id, name, role: null, segments_count: 0 }))
        if (restored.length > 0) {
          setCustomSpeakers(restored)
        }
      }
    }
  }, [savedSpeakerNames, transcript?.speakers])

  // Edit mutation (optimistic)
  const editMutation = useMutation({
    mutationFn: ({ index, field, value }: { index: number; field: 'text' | 'speaker'; value: string }) =>
      editSegment(projectId!, index, field, value),
    onMutate: async ({ index, field, value }) => {
      await queryClient.cancelQueries({ queryKey: ['transcript', projectId] })
      const prev = queryClient.getQueryData<Transcript>(['transcript', projectId])
      if (prev) {
        const newSegments = prev.segments.map((seg, i) => {
          if (i !== index) return seg
          if (field === 'text') return { ...seg, text: value }
          return { ...seg, speaker: value, speaker_id: value }
        })
        queryClient.setQueryData(['transcript', projectId], {
          ...prev, segments: newSegments,
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['transcript', projectId], ctx.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript', projectId] })
      queryClient.invalidateQueries({ queryKey: ['transcriptAnalysis', projectId] })
    },
  })

  // Split mutation (optimistic)
  const splitMutation = useMutation({
    mutationFn: ({ index, charPosition }: { index: number; charPosition: number }) =>
      splitSegment(projectId!, index, charPosition),
    onMutate: async ({ index, charPosition }) => {
      await queryClient.cancelQueries({ queryKey: ['transcript', projectId] })
      const prev = queryClient.getQueryData<Transcript>(['transcript', projectId])
      if (prev) {
        const seg = prev.segments[index]
        const textLeft = seg.text.substring(0, charPosition).trimEnd()
        const textRight = seg.text.substring(charPosition).trimStart()
        const ratio = textLeft.length / seg.text.length
        const splitTime = seg.start + (seg.end - seg.start) * ratio

        const leftSeg = { ...seg, text: textLeft, end: splitTime }
        const rightSeg = { ...seg, text: textRight, start: splitTime }

        const newSegments = [
          ...prev.segments.slice(0, index),
          leftSeg,
          rightSeg,
          ...prev.segments.slice(index + 1),
        ]

        const newSections = prev.sections?.map(s => ({
          ...s,
          start_segment: s.start_segment > index ? s.start_segment + 1 : s.start_segment,
          end_segment: s.end_segment >= index ? s.end_segment + 1 : s.end_segment,
        }))

        queryClient.setQueryData(['transcript', projectId], {
          ...prev, segments: newSegments, sections: newSections ?? null,
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['transcript', projectId], ctx.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript', projectId] })
      queryClient.invalidateQueries({ queryKey: ['transcriptAnalysis', projectId] })
    },
  })

  // Speaker rename mutation (optimistic)
  const renameSpeakerMutation = useMutation({
    mutationFn: ({ speakerId, name }: { speakerId: string; name: string }) =>
      setSpeakerName(projectId!, speakerId, name),
    onMutate: async ({ speakerId, name }) => {
      await queryClient.cancelQueries({ queryKey: ['speakerNames', projectId] })
      const prev = queryClient.getQueryData<Record<string, string>>(['speakerNames', projectId])
      if (prev) {
        queryClient.setQueryData(['speakerNames', projectId], { ...prev, [speakerId]: name })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['speakerNames', projectId], ctx.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['speakerNames', projectId] })
    },
  })

  // Word correction mutation (optimistic)
  const wordCorrectionMutation = useMutation({
    mutationFn: ({ segmentIndex, wordIndex, correctedTo }: {
      segmentIndex: number
      wordIndex: number
      correctedTo: string
    }) => correctWord(projectId!, segmentIndex, wordIndex, correctedTo),
    onMutate: async ({ segmentIndex, wordIndex, correctedTo }) => {
      await queryClient.cancelQueries({ queryKey: ['transcriptAnalysis', projectId] })
      const prevAnalysis = queryClient.getQueryData<TranscriptAnalysis>(['transcriptAnalysis', projectId])
      if (prevAnalysis) {
        const newSegments = prevAnalysis.segments.map(seg => {
          if (seg.index !== segmentIndex || !seg.words) return seg
          const newWords = seg.words.map((w, j) =>
            j === wordIndex ? { ...w, corrected_to: correctedTo } : w
          )
          const newText = newWords.map(w => w.corrected_to || w.text).join(' ')
          return { ...seg, words: newWords, text: newText }
        })
        queryClient.setQueryData(['transcriptAnalysis', projectId], {
          ...prevAnalysis, segments: newSegments,
        })
      }

      await queryClient.cancelQueries({ queryKey: ['transcript', projectId] })
      const prevTranscript = queryClient.getQueryData<Transcript>(['transcript', projectId])
      if (prevTranscript && prevAnalysis) {
        const seg = prevAnalysis.segments.find(s => s.index === segmentIndex)
        if (seg?.words) {
          const newText = seg.words.map((w, j) =>
            j === wordIndex ? correctedTo : (w.corrected_to || w.text)
          ).join(' ')
          const newSegments = prevTranscript.segments.map((s, i) =>
            i === segmentIndex ? { ...s, text: newText } : s
          )
          queryClient.setQueryData(['transcript', projectId], {
            ...prevTranscript, segments: newSegments,
          })
        }
      }

      return { prevAnalysis, prevTranscript }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevAnalysis) queryClient.setQueryData(['transcriptAnalysis', projectId], ctx.prevAnalysis)
      if (ctx?.prevTranscript) queryClient.setQueryData(['transcript', projectId], ctx.prevTranscript)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptAnalysis', projectId] })
      queryClient.invalidateQueries({ queryKey: ['transcript', projectId] })
    },
  })

  // Abbreviation update mutation (optimistic)
  const abbreviationMutation = useMutation({
    mutationFn: ({ abbreviationId, displayMode }: {
      abbreviationId: number
      displayMode: 'short' | 'full' | 'both'
    }) => updateAbbreviationMode(projectId!, abbreviationId, displayMode),
    onMutate: async ({ abbreviationId, displayMode }) => {
      await queryClient.cancelQueries({ queryKey: ['transcriptAnalysis', projectId] })
      const prev = queryClient.getQueryData<TranscriptAnalysis>(['transcriptAnalysis', projectId])
      if (prev?.abbreviations) {
        const newAbbreviations = prev.abbreviations.map(a =>
          a.id === abbreviationId ? { ...a, display_mode: displayMode } : a
        )
        queryClient.setQueryData(['transcriptAnalysis', projectId], {
          ...prev, abbreviations: newAbbreviations,
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['transcriptAnalysis', projectId], ctx.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptAnalysis', projectId] })
    },
  })

  const handleEdit = (index: number, field: 'text' | 'speaker', value: string) => {
    editMutation.mutate({ index, field, value })
  }

  const handleSplit = (index: number, charPosition: number) => {
    splitMutation.mutate({ index, charPosition })
  }

  const handleRenameSpeaker = (speakerId: string, newName: string) => {
    setSpeakerNames(prev => ({ ...prev, [speakerId]: newName }))
    renameSpeakerMutation.mutate({ speakerId, name: newName })
  }

  const handleWordCorrect = (segmentIndex: number, wordIndex: number, correctedTo: string) => {
    wordCorrectionMutation.mutate({ segmentIndex, wordIndex, correctedTo })
  }

  const handleAbbreviationModeUpdate = (abbreviationId: number, displayMode: 'short' | 'full' | 'both') => {
    abbreviationMutation.mutate({ abbreviationId, displayMode })
  }

  const [customSpeakers, setCustomSpeakers] = useState<Array<{ id: string; name: string | null; role: string | null; segments_count: number }>>([])

  const handleAddSpeaker = (name: string) => {
    const newId = `S-${(transcript?.speakers?.length || 0) + customSpeakers.length}`
    setCustomSpeakers(prev => [...prev, { id: newId, name, role: null, segments_count: 0 }])
    setSpeakerNames(prev => ({ ...prev, [newId]: name }))
    renameSpeakerMutation.mutate({ speakerId: newId, name })
  }

  // Merge transcript speakers with custom speakers
  const allSpeakers = [
    ...(transcript?.speakers || []),
    ...customSpeakers
  ]

  const handleExport = (format: 'txt' | 'srt') => {
    if (!transcript) return
    const content = exportTranscript(transcript, format, speakerNames)
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transcript.${format}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (projectLoading || transcriptLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (projectError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Ошибка доступа</h1>
          <p className="text-gray-600">Ссылка недействительна или срок её действия истёк.</p>
          <p className="text-gray-500 text-sm mt-4">Запросите новую ссылку в Telegram-боте.</p>
        </div>
      </div>
    )
  }

  if (!transcript) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Транскрипт не найден</h1>
          <p className="text-gray-600">Возможно, обработка ещё не завершена.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      {/* Banner */}
      <div className="flex items-center gap-4 mb-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
        <img src="/logo.png" alt="Fluffy Fox Ear" className="w-20 h-auto" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800 tracking-wide">FLUFFY-FOX-EAR</h1>
          <p className="text-gray-500 text-sm">Сайт защиты диссертаций от лис</p>
        </div>
      </div>

      {/* Project Header */}
      <header className="mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800">
              {project?.title || `Проект #${projectId}`}
            </h2>
            <p className="text-gray-500 text-sm">
              {transcript.segments.length} сегментов • версия {transcript.version}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('txt')}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Экспорт TXT
            </button>
            <button
              onClick={() => handleExport('srt')}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Экспорт SRT
            </button>
          </div>
        </div>
      </header>

      {/* Audio Player */}
      {audioData?.url && (
        <AudioPlayer
          url={audioData.url}
          segments={transcript.segments.map(s => ({ start: s.start, end: s.end }))}
        />
      )}

      {/* Speakers and Abbreviations */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SpeakerList
          speakers={allSpeakers}
          speakerNames={speakerNames}
          onRename={handleRenameSpeaker}
          onAdd={handleAddSpeaker}
        />
        {analysis?.abbreviations && analysis.abbreviations.length > 0 && (
          <AbbreviationPanel
            abbreviations={analysis.abbreviations}
            onUpdateMode={handleAbbreviationModeUpdate}
          />
        )}
      </div>

      {/* Analysis info banner */}
      {analysis && (
        <div className="mt-4 bg-blue-50 rounded-lg p-3 flex items-center gap-3">
          <div className="flex-1">
            {analysis.subject_area && (
              <span className="text-sm text-blue-800">
                <strong>Область:</strong> {analysis.subject_area}
              </span>
            )}
            {analysis.document_type && (
              <span className="text-sm text-blue-800 ml-4">
                <strong>Тип:</strong> {analysis.document_type}
              </span>
            )}
          </div>
          {analysis.corrections_pending && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
              Есть несохранённые исправления
            </span>
          )}
          {analysis.analysis_complete && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
              Анализ завершён
            </span>
          )}
        </div>
      )}

      {/* Word correction legend */}
      {analysis?.segments?.some(s => s.words && s.words.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span>
            Низкая точность
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span>
            Термин
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span>
            Имя
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span>
            Аббревиатура
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span>
            Исправлено
          </span>
        </div>
      )}

      {/* Transcript */}
      <div className="mt-4 bg-white rounded-lg shadow">
        <TranscriptList
          transcript={transcript}
          speakers={allSpeakers}
          speakerNames={speakerNames}
          segmentsWithWords={analysis?.segments}
          onEdit={handleEdit}
          onSplit={handleSplit}
          onWordCorrect={handleWordCorrect}
        />
      </div>

      {/* Save indicator */}
      {(editMutation.isPending || splitMutation.isPending || wordCorrectionMutation.isPending || abbreviationMutation.isPending) && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          Сохранение...
        </div>
      )}
    </div>
  )
}
